/**
 * 3D turbine geometry for the scoping scenario (parent spec §7, GOAL.md
 * visual bar). Purely illustrative massing — a tapered tower, a nacelle,
 * and a three-blade rotor at hub height. This has NO bearing on any LOS
 * result (hard constraint #1: LOS truth is raypath's analytical raster);
 * these meshes are scene furniture only.
 *
 * Vertical placement uses each turbine's published `ground_elev_m_aod`
 * (DTM-derived, resolved by the pipeline) plus the scenario's hub height —
 * we never sample the interactive mesh collider for turbine bases.
 */
import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  SphereGeometry,
} from 'three';
import { bngToLocal } from '../core/coordinates';
import type { TurbineScenario } from '../core/manifest';

/**
 * Onshore blade-tip speed cap (m/s). Onshore turbines are noise-constrained,
 * so tip speeds sit below the ~90–95 m/s offshore range. This is the single
 * knob that sets rotor RPM; it is illustrative, not a published Ray II figure.
 */
export const TIP_SPEED_CAP_MS = 80;

/** Nominal rotor angular speed (rad/s) from a constant tip-speed cap. */
export function rotorAngularSpeed(rotorRadiusM: number, tipSpeedCapMs = TIP_SPEED_CAP_MS): number {
  return tipSpeedCapMs / rotorRadiusM;
}

/** Deterministic fractional hash in [0, 1) seeded by an integer. */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Deterministic initial blade angle (rad) for turbine index `i`, in [0, 2π). */
export function turbinePhase(i: number): number {
  return hash01(i) * 2 * Math.PI;
}

/** Per-turbine angular speed: nominal ±3%, deterministic by index. */
export function turbineAngularSpeed(rotorRadiusM: number, i: number, tipSpeedCapMs = TIP_SPEED_CAP_MS): number {
  const nominal = rotorAngularSpeed(rotorRadiusM, tipSpeedCapMs);
  const jitter = (hash01(i + 100) * 2 - 1) * 0.03;
  return nominal * (1 + jitter);
}

/**
 * Tower base/top radii (m) derived from hub height. Base diameter ≈ hub × 0.04
 * (~5.5 m at a 137 m hub) reflects structural sizing of a large onshore tower;
 * the top tapers to 60% of the base. Illustrative proportions, not published.
 */
export function towerRadii(hubHeightM: number): { baseR: number; topR: number } {
  const baseR = (hubHeightM * 0.04) / 2;
  return { baseR, topR: baseR * 0.6 };
}

/**
 * Nacelle length/width/height (m) derived from rotor diameter, using ~8 MW-class
 * proportions (length ≈ 11% of rotor diameter → ~20 m; width/height ≈ 4.3% →
 * ~8 m). Illustrative proportions, not published Ray II figures.
 */
export function nacelleDimensions(rotorDiameterM: number): { length: number; width: number; height: number } {
  return {
    length: rotorDiameterM * 0.11,
    width: rotorDiameterM * 0.043,
    height: rotorDiameterM * 0.043,
  };
}

/**
 * Hub/spinner dimensions (m) derived from nacelle width and rotor radius, using
 * ~8 MW-class proportions: a rounded fairing ≈0.7 × nacelle width across (~5.6 m)
 * and slightly longer fore-aft, with blade-root stubs matching the blade root
 * width. Illustrative proportions, not published Ray II figures.
 */
export function hubDimensions(
  nacelleWidthM: number,
  rotorRadiusM: number,
): { fairingRadius: number; fairingLength: number; stubRadius: number; stubLength: number } {
  const fairingRadius = nacelleWidthM * 0.35;
  return {
    fairingRadius,
    fairingLength: fairingRadius * 2 * 1.2,
    stubRadius: rotorRadiusM * 0.02,
    stubLength: rotorRadiusM * 0.03,
  };
}

/**
 * Drives rotor spin. Holds each rotor Object3D + its angular speed and advances
 * rotation.z per frame. Pure scene animation — no bearing on any LOS result.
 */
export class RotorAnimator {
  paused = false;
  private rotors: Array<{ obj: Object3D; omega: number }> = [];

  add(obj: Object3D, omega: number): void {
    this.rotors.push({ obj, omega });
  }

  update(dt: number): void {
    if (this.paused) return;
    for (const r of this.rotors) {
      r.obj.rotation.z += r.omega * dt;
    }
  }

  get count(): number {
    return this.rotors.length;
  }
}

export interface TurbinePlacement {
  /** local viewer-space east offset (m) */
  x: number;
  /** tower base elevation, metres AOD */
  y: number;
  /** local viewer-space north offset (m) */
  z: number;
  label: string;
}

/**
 * Resolves each turbine to its base placement in local viewer space. Pure
 * (no three.js), so it can be unit-tested against the published scenario
 * coordinates without a renderer.
 */
export function turbinePlacements(scenario: TurbineScenario): TurbinePlacement[] {
  return scenario.turbines.map((t) => {
    const { x, z } = bngToLocal(t.easting, t.northing);
    return { x, y: t.ground_elev_m_aod, z, label: t.id };
  });
}

/** Mid-grey proposed (Ray II) colours — contrast against horizon haze at ~9 km (issue #175). */
export const PROPOSED_TOWER_COLOR = 0x969b9f;
export const PROPOSED_BLADE_COLOR = 0x8f9499;

const TOWER_MATERIAL = new MeshStandardMaterial({ color: PROPOSED_TOWER_COLOR, roughness: 0.55, metalness: 0.1 });
const BLADE_MATERIAL = new MeshStandardMaterial({ color: PROPOSED_BLADE_COLOR, roughness: 0.5, metalness: 0.05 });

/**
 * Operational-farm (Ray I, as-built) shared materials: a muted grey so the
 * real, built turbines read as visually distinct from the proposed Ray II
 * (mid-grey) turbines at a glance, matching the HUD legend swatches
 * (issue #43). Two more module-level constants, reused across every Ray I
 * turbine — never allocated per turbine (issue #17 perf budget: fixed at
 * four shared materials total, not O(turbine count)).
 */
const OPERATIONAL_TOWER_MATERIAL = new MeshStandardMaterial({ color: 0x8a8f94, roughness: 0.65, metalness: 0.08 });
const OPERATIONAL_BLADE_MATERIAL = new MeshStandardMaterial({ color: 0x7d8388, roughness: 0.6, metalness: 0.05 });

export type TurbineMaterialVariant = 'proposed' | 'operational';

/**
 * Which shared material set a scenario's turbines render with. The real,
 * operational Ray I farm (`coordinate_status === "osm_asbuilt"`) renders
 * "operational" (muted grey); every size-variant/proposed scenario — Ray II
 * scoping, and the Ray One turbine-SIZE stand-in that reuses Ray II's
 * positions — renders "proposed" (mid-grey). Reads a real
 * field from the resolved data rather than inferring from id/label (hard
 * constraint #7: never fabricate).
 */
export function turbineMaterialVariant(scenario: Pick<TurbineScenario, 'coordinate_status'>): TurbineMaterialVariant {
  return scenario.coordinate_status === 'osm_asbuilt' ? 'operational' : 'proposed';
}

function materialsForVariant(variant: TurbineMaterialVariant): {
  tower: MeshStandardMaterial;
  blade: MeshStandardMaterial;
} {
  return variant === 'operational'
    ? { tower: OPERATIONAL_TOWER_MATERIAL, blade: OPERATIONAL_BLADE_MATERIAL }
    : { tower: TOWER_MATERIAL, blade: BLADE_MATERIAL };
}

/**
 * Builds one turbine model, its base at the group origin (y = 0), reaching up
 * to `hubHeight` with a rotor of `rotorRadius`. Tower and nacelle are scaled
 * from the turbine specs (see towerRadii / nacelleDimensions). Returns the
 * group and the rotor sub-group (named 'rotor') so the caller can animate it.
 * `materials` is one of the two fixed, shared material pairs (see
 * materialsForVariant) — never allocated here, so building N turbines never
 * allocates more than the two materials already shared across the group.
 */
function buildTurbine(
  hubHeight: number,
  rotorRadius: number,
  rotorDiameter: number,
  materials: { tower: MeshStandardMaterial; blade: MeshStandardMaterial },
): { group: Group; rotor: Group } {
  const g = new Group();

  const { baseR, topR } = towerRadii(hubHeight);
  const tower = new Mesh(new CylinderGeometry(topR, baseR, hubHeight, 16), materials.tower);
  tower.position.y = hubHeight / 2;
  tower.castShadow = true;
  tower.receiveShadow = true;
  g.add(tower);

  const nd = nacelleDimensions(rotorDiameter);
  const nacelle = new Mesh(new BoxGeometry(nd.width, nd.height, nd.length), materials.tower);
  nacelle.position.y = hubHeight;
  nacelle.castShadow = true;
  g.add(nacelle);

  const rotor = new Group();
  rotor.name = 'rotor';
  rotor.position.y = hubHeight;
  rotor.position.z = nd.length / 2;

  const hub = hubDimensions(nd.width, rotorRadius);

  // Spinner fairing: a rounded bullet (a sphere scaled along the rotor axis z).
  // Centred on the rotor plane (z = 0 in rotor space, i.e. the nacelle front
  // face) so its widest cross-section sits exactly where the blade roots attach;
  // the nose then pokes forward past the nacelle and the rear tucks inside it.
  // Broad massing, not a cowling.
  const fairing = new Mesh(new SphereGeometry(hub.fairingRadius, 16, 12), materials.tower);
  fairing.scale.z = hub.fairingLength / (2 * hub.fairingRadius);
  fairing.castShadow = true;
  rotor.add(fairing);

  const bladeWidth = Math.max(1.5, rotorRadius * 0.04);
  const bladeStart = hub.fairingRadius;          // blades emerge from the hub surface
  const bladeLength = rotorRadius - bladeStart;  // tip still reaches rotorRadius
  for (let i = 0; i < 3; i++) {
    const pivot = new Object3D();
    pivot.rotation.z = (i * 2 * Math.PI) / 3;

    // Blade-root stub: short cylinder (default +y axis = radial) emerging from
    // the fairing surface, where a real blade bolts into the hub.
    const stub = new Mesh(
      new CylinderGeometry(hub.stubRadius, hub.stubRadius, hub.stubLength, 12),
      materials.tower,
    );
    stub.position.y = hub.fairingRadius + hub.stubLength / 2;
    stub.castShadow = true;
    pivot.add(stub);

    const blade = new Mesh(new BoxGeometry(bladeWidth, bladeLength, bladeWidth * 0.4), materials.blade);
    blade.position.y = bladeStart + bladeLength / 2;
    blade.castShadow = true;
    pivot.add(blade);

    rotor.add(pivot);
  }

  g.add(rotor);

  return { group: g, rotor };
}

/**
 * Builds a Group holding every turbine in the scenario (each child named
 * `turbine-<id>` and positioned at its base placement) plus a RotorAnimator
 * driving each rotor. Each rotor starts at its deterministic phase and spins
 * at its per-turbine angular speed.
 */
export function buildTurbines(scenario: TurbineScenario): { group: Group; animator: RotorAnimator } {
  const root = new Group();
  root.name = `turbines-${scenario.id}`;
  const animator = new RotorAnimator();
  const placements = turbinePlacements(scenario);
  const materials = materialsForVariant(turbineMaterialVariant(scenario));
  placements.forEach((p, i) => {
    const { group, rotor } = buildTurbine(
      scenario.hub_height_m,
      scenario.rotor_radius_m,
      scenario.rotor_diameter_m,
      materials,
    );
    group.name = `turbine-${p.label}`;
    group.position.set(p.x, p.y, p.z);
    rotor.rotation.z = turbinePhase(i);
    animator.add(rotor, turbineAngularSpeed(scenario.rotor_radius_m, i));
    root.add(group);
  });
  return { group: root, animator };
}

/**
 * Splits the resolved turbine scenarios into the size-variant scenarios that
 * populate the `#turbine-select` dropdown (Ray II scoping, and the Ray One
 * turbine-SIZE stand-in reusing Ray II's positions) and the single real,
 * operational Ray I farm (`coordinate_status === "osm_asbuilt"`), which
 * renders as a separate always-on layer and must never appear as a dropdown
 * option (issue #43). `operational` is `undefined`, not fabricated, when no
 * osm_asbuilt scenario is present (hard constraint #7).
 */
export function splitTurbineScenarios(scenarios: TurbineScenario[]): {
  sizeVariants: TurbineScenario[];
  operational: TurbineScenario | undefined;
} {
  const sizeVariants = scenarios.filter((s) => s.coordinate_status !== 'osm_asbuilt');
  const operational = scenarios.find((s) => s.coordinate_status === 'osm_asbuilt');
  return { sizeVariants, operational };
}
